/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, April 3rd 2022, 4:01:58 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import * as uuid from "uuid";
import { PrimaryKeyValue, ObjectKey } from "lib/types";
import {
  checkItemIsInList,
  appendItemToList,
  replaceItemInList,
  getItemsWithNewPositions,
  immutableShift,
} from "lib/utils";
import { LIST_EDITOR_ACTIONS, ItemsListReducerAction, CreationQueueItem } from "./list-editor-actions";

/**
 * Reducer State Explained:
 *
 * selectedItemId: The selected item Id
 * searchCriteria: The search criteria for the list of items
 * savedItems: All the items that are part of the editing scope. These include items that do not meet any filtering condition(s). All items here must have unique stable ids that we leave the API responsible for creating
 * itemCreationQueue: So that our savedItems remains only with items that have ids and so that we leave the responsbility of assigning ids to the API, we have this itemCreationQueue that is responsible for storing new items that are to be sent to the API for saving. We dont want to deal with the mess of mixing up new items with no stable ids with stable items.
 * itemsLoaded: A value to indicate whether the items have been loaded so we can distinguish between a truly empty list and a list that is only empty because it is being populated after a fetch from the API.
 */

export interface ItemsListReducerState<T> {
  savedItems: T[];
  itemCreationQueue: CreationQueueItem<T>[];
  itemsLoaded: boolean;
  selectedItemId: PrimaryKeyValue<T>;
  filteredItemIds: PrimaryKeyValue<T>[];
  config: { uniqueIdentifier: ObjectKey<T>; positionLabel: ObjectKey<T> };
}

export type ItemsListReducer<T> = (
  previousState: ItemsListReducerState<T>,
  action: ItemsListReducerAction<T>
) => ItemsListReducerState<T>;

export const itemsListReducer = <T>(
  previousState: ItemsListReducerState<T>,
  action: ItemsListReducerAction<T>
): ItemsListReducerState<T> => {
  switch (action.actionType) {
    case LIST_EDITOR_ACTIONS.LOAD_ITEMS:
      return {
        ...previousState,
        savedItems: action.payload.fetchedItems ?? [],
        itemsLoaded: true,
      };

    case LIST_EDITOR_ACTIONS.SELECT_ITEM:
      return {
        ...previousState,
        selectedItemId: action.payload.selectedItemId,
      };

    case LIST_EDITOR_ACTIONS.REMOVE_ITEM:
      return {
        ...previousState,
        savedItems: previousState.savedItems.filter(
          (item) => item[previousState.config.uniqueIdentifier] !== action.payload.itemToRemoveId
        ),
      };

    case LIST_EDITOR_ACTIONS.UPDATE_LIST:
      const itemIsInList = checkItemIsInList(
        action.payload.itemToUpdate,
        previousState.savedItems,
        previousState.config.uniqueIdentifier
      );

      const updatedItemsList = itemIsInList
        ? replaceItemInList(
            action.payload.itemToUpdate,
            previousState.savedItems,
            previousState.config.uniqueIdentifier,
            previousState.config.positionLabel
          )
        : appendItemToList(
            action.payload.itemToUpdate,
            previousState.savedItems,
            previousState.config.positionLabel,
            previousState.config.uniqueIdentifier
          );

      return {
        ...previousState,
        savedItems: updatedItemsList,
        itemCreationQueue: itemIsInList
          ? previousState.itemCreationQueue
          : updateCreationQueue(previousState.itemCreationQueue, action.payload.options?.newItemToRemoveTempId),
      };

    case LIST_EDITOR_ACTIONS.CREATE_ITEM:
      // CREATE_ITEM will trigger an API POST via useEffect in calling components. So components using this
      // provider should post the new item when id is null, which is the case with our useAPIForm hook.

      // os_unsaved_item_temp_unique_id is used so that this object can be uniquely identified in any
      // dependency lists either using a deep comparison or JSON.stringify. This is necessary since a new
      // unsaved item has no id until it is allocated by the api. Without this, it would be close to impossible
      // to add multiple objects with the same default values simulataneously without assigning an id attribute.
      // Since we want the API to have the sole responsibility of creating a unique primary_key, we
      // use os_unsaved_item_temp_unique_id as a temporary id for an unsaved item.

      return {
        ...previousState,
        itemCreationQueue: [
          ...previousState.itemCreationQueue,
          { ...action.payload.newItem, os_unsaved_item_temp_unique_id: uuid.v4() },
        ],
      };

    case LIST_EDITOR_ACTIONS.DEQUEUE_NEW_ITEM:
      return {
        ...previousState,
        itemCreationQueue: previousState.itemCreationQueue.filter(
          (item) => item.os_unsaved_item_temp_unique_id !== action.payload.newItemTempId
        ),
      };

    case LIST_EDITOR_ACTIONS.FILTER_LIST:
      return {
        ...previousState,
        filteredItemIds: action.listFilterFunction(previousState.savedItems),
      };

    case LIST_EDITOR_ACTIONS.HANDLE_DRAG_ENTER:
      // Action for handling the hovering of a dragged item over a valid drop target. This action is fired as a callback
      // to react dnd hover method that is an implementation of the DOM dragenter event
      // https://developer.mozilla.org/en-US/docs/Web/API/Document/dragenter_event

      // Making sure we store dragSourcePosition by reference so that our function to change item
      // position is a pure function.
      const dragSourcePosition = previousState.savedItems.find(
        (tt) => tt[previousState.config.uniqueIdentifier] === action.payload.sourceItemId
      )?.[previousState.config.positionLabel];

      return {
        ...previousState,
        savedItems: getItemsWithNewPositions(
          previousState.savedItems,
          dragSourcePosition,
          action.payload.hoverPosition,
          previousState.config.positionLabel,
          previousState.config.uniqueIdentifier
        ),
      };
  }
};

const updateCreationQueue = <T>(
  itemCreationQueue: CreationQueueItem<T>[],
  newItemToRemoveTempId: CreationQueueItem<T>["os_unsaved_item_temp_unique_id"]
) => {
  // If item was not in previous lists, this is a new item so we use the os_unsaved_item_temp_unique_id
  // attribute or a fallback FIFO to clear the corresponding item on the creation queue.
  if (newItemToRemoveTempId)
    return itemCreationQueue.filter((item) => item.os_unsaved_item_temp_unique_id !== newItemToRemoveTempId);

  return immutableShift(itemCreationQueue);
};
